import "./notices.less";
import template from "./notices.html";
import {
	FuzzySearcher,
	FUZZY_OPTIONS
} from "../../utils/fuzzy-search";
import { globs } from "../../services/globs";
import {
	ActionOrPattern,
	Any
} from "../../services/security/actionScopes";
import moment from "moment";
import noticesFilters from "./notices-filters/notices-filters.js";
import noticesTable from "./notices-table/notices-table.js";
import noticeModal from "./notice-modal/notice-modal.js";

function controller(
	appState,
	loadingService,
	tableHelper,
	$uibModal,
	$logger,
	noticeAdminService,
	toastr,
	$scope,
	$location,
	$stateParams
) {
	const ctrl = this;

	ctrl.promiseTracker = loadingService.getTracker();

	ctrl.tableWidthCss = "col-sm-9";
	ctrl.onCollapse = onCollapse;

	ctrl.canCreateNotice = globs.notices.create;
	ctrl.canUpdateNotice = globs.notices.update;
	ctrl.hasAnyAction = new Any([
		new ActionOrPattern(ctrl.canCreateNotice)
	]);

	ctrl.searcher = new FuzzySearcher([], FUZZY_OPTIONS.NOTICES);

	ctrl.filters = {
		search: null,
		severity: "",
		status: "active"
	};

	ctrl.table = {
		data: [],
		allData: [],
		actions: { sort: () => {} },
		sort: {
			predicate: "BeginInclusive",
			direction: "desc"
		},
		filter: () => {
			const filtered = applyLocalFilters(ctrl.table.allData);
			ctrl.searcher.setCollection(filtered);
			tableHelper.filter(ctrl.table, ctrl.searcher, {
				defaultSortBy: "BeginInclusive",
				logger: $logger
			});
			ctrl.paginatorOptions = {
				...ctrl.paginatorOptions,
				totalItems: ctrl.table.data.length
			};
		},
		reset: () => reset(),
		filterBy: null
	};
	ctrl.paginatorOptions = { currentPage: 1, itemsPerPage: 10, totalItems: 0 };

	ctrl.$onInit = () => {
		ctrl.form = appState.forms.selected;
		refresh();

		$scope.$watch(() => ctrl.table.filterBy, (newValue, oldValue) => {
			if(newValue !== oldValue) {
				$location.search("search", newValue || null).replace();
				ctrl.table.filter();
			}
		});

		$scope.$on("$locationChangeSuccess", () => {
			const searchParams = $location.search();
			handleStateChange({
				search: searchParams.search,
				id: searchParams.id,
				action: searchParams.action
			});
		});
	};

	ctrl.onFilterChange = (filters) => {
		ctrl.filters = filters;
		ctrl.table.filterBy = filters.search;
		ctrl.table.filter();
	};

	ctrl.viewNotice = (notice) => {
		openModal(notice, "view");
	};

	ctrl.createNotice = () => {
		openModal(null, "create");
	};

	ctrl.expireNotice = (notice) => {
		const payload = {
			Title: notice.Title,
			Content: notice.Content,
			BeginInclusive: notice.BeginInclusive,
			EndExclusive: moment().toISOString(),
			Interval: notice.Interval || null,
			Severity: { Code: notice.Severity?.Code }
		};

		const promise = noticeAdminService.updateNotice(ctrl.form.Id, notice.Id, payload)
			.then(() => {
				toastr.success("Notice expired successfully.");
				return getNotices();
			})
			.catch((err) => {
				$logger.error("Failed to expire notice", err);
				toastr.error("Failed to expire notice.");
			});
		ctrl.promiseTracker.addPromise(promise);
	};

	async function refresh() {
		await getNotices();
		handleStateChange({
			search: $stateParams.search,
			id: $stateParams.id,
			action: $stateParams.action
		});
	}

	function handleStateChange({
		search, 
		id, 
		action 
	} = {}) {
		if(search !== ctrl.table.filterBy) {
			ctrl.table.filterBy = search || null;
		}
		handleModalState({ id, action });
	}

	let currentModalId = null;

	function handleModalState({ id, action } = {}) {
		if(currentModalId !== null) {
			return;
		}
		if(id) {
			if($location.search().action) {
				$location.search("action", null).replace();
			}
			const notice = ctrl.table.allData.find(n => n.Id == id);
			if(notice) {
				openModal(notice, "view");
			}
			else {
				resetModal();
			}
		}
		else if(action === "add") {
			openModal(null, "create");
		}
	}

	async function openModal(notice, mode) {
		currentModalId = $location.search().id || null;
		try {
			await $uibModal.open({
				component: "noticeModal",
				size: "lg",
				resolve: {
					notice: () => notice,
					mode: () => mode
				}
			}).result;

			await getNotices();
			ctrl.table.filter();
		}
		catch(e) {
			// modal dismissed
		}
		finally {
			currentModalId = null;
			resetModal();
		}
	}

	function resetModal() {
		$location
			.search("id", null)
			.search("action", null)
			.hash("")
			.replace();
	}

	async function getNotices() {
		const promise = noticeAdminService.getNotices(ctrl.form.Id)
			.then(notices => {
				ctrl.table.allData = notices;
				ctrl.table.data = notices;
				ctrl.searcher.setCollection(notices);
				ctrl.table.filter();
			});

		ctrl.promiseTracker.addPromise(promise);
		return promise;
	}

	function applyLocalFilters(data) {
		let filtered = data;

		if(ctrl.filters.severity) {
			filtered = filtered.filter(n => n.Severity?.Code === ctrl.filters.severity);
		}

		if(ctrl.filters.status) {
			const now = moment();
			filtered = filtered.filter(n => {
				const begin = moment(n.BeginInclusive);
				const end = n.EndExclusive ? moment(n.EndExclusive) : null;

				switch(ctrl.filters.status) {
					case "active":
						return begin.isSameOrBefore(now) && (!end || end.isAfter(now));
					case "scheduled":
						return begin.isAfter(now);
					case "expired":
						return end && end.isBefore(now);
					default:
						return true;
				}
			});
		}

		return filtered;
	}

	function reset() {
		ctrl.table.filterBy = null;
		ctrl.filters = { search: null, severity: "", status: "" };
		ctrl.table.filter();
	}

	function onCollapse() {
		ctrl.tableWidthCss = ctrl.isCollapsed ? "col-sm-12" : "col-sm-9";
	}
}

export default angular
	.module("vesl.screens.notices", [noticesFilters, noticesTable, noticeModal])
	.component("notices", {
		template,
		controller
	}).name;
